import { webStorage } from 'sidecar-utility-library'
import { isNil, isEmpty } from 'ramda'

import { isActionRelevant } from './utils'
import { fetchRatingArea } from 'store/Care'
import { storeAuthedUser } from 'store/Auth'
import { setChatAttributes } from 'utils/chat/useChatWidget'
import {
  buildMemberAppSession,
  syncMemberAppSession,
} from 'utils/memberAppSession'

/** Handles the side-effects of members being cancelled. */
export const handleCancellation = () => next => action => {
  const relevantActions = /CANCEL_MEMBER_SUCCESS/
  if (!isActionRelevant(action, relevantActions)) return next(action)

  webStorage.setItem('hasPolicy', false)
  webStorage.removeItem('ptnr')
  return next(action)
}

/** Sends member info to heap when we fetch their info */
export const addHeapUserProperties = () => next => action => {
  const relevantActions = /FETCH_MEMBERS_SUCCESS/
  if (!isActionRelevant(action, relevantActions)) return next(action)

  const [member] = action.payload

  // in the cases where the admin has linked to the member app, but is
  // not quite impersonating a member, we won't have a member to work with
  // so we should just bail out of this middleware. Nothing to do here.
  if (!member) return next(action)

  // setting hasPolicy here is important to make sure that the global nav
  // shows the right links. Without this, the global nav is especially
  // screwy for impersonating admins because they bypass all the other
  // opportunities to set hasPolicy.
  if (member.policy) {
    webStorage.setItem('hasPolicy', true)
  }

  // we're only going to be using the static properties here.
  // See `withHeap` to learn more about our event properties.
  window.heap.addUserProperties({
    accountUuid: webStorage.getItem('aud'),
    memberUuid: member.uuid,
  })

  return next(action)
}

/** Stores global vars for WebMessenger so agents know about our members. */
export const addWebMessengerUserProperties = () => next => action => {
  const relevantActions = /FETCH_AUTHED_USER_SUCCESS/
  if (!isActionRelevant(action, relevantActions)) return next(action)

  const member = action.payload

  // in the cases where the admin has linked to the member app, but is
  // not quite impersonating a member, we won't have a member to work with
  // so we should just bail out of this middleware. Nothing to do here.
  if (!member) return next(action)

  const productType = member.policy
    ? member.policy.insuranceProduct
    : 'NO_POLICY_FOUND'
  const companyName = member.asoGroupDto ? member.asoGroupDto.name : null

  //This is the full set of values Genesys needs to route a member to the correct queue
  const chatAttrs = {
    firstName: member.firstName,
    lastName: member.lastName,
    companyName,
    dateOfBirth: member.dateOfBirth,
    email: member.email,
    memberUuid: member.uuid,
    phoneNumber: member.phoneNumber,
    productType,
  }

  setChatAttributes(chatAttrs)
  return next(action)
}

/**
 * Stores the members rating area which is used to fetch Contenful content in
 * care search
 */
export const getRatingArea =
  ({ dispatch }) =>
  next =>
  action => {
    const relevantActions = /FETCH_AUTHED_USER_SUCCESS/
    if (!isActionRelevant(action, relevantActions)) return next(action)

    const member = action.payload

    // in the cases where the admin has linked to the member app, but is
    // not quite impersonating a member, we won't have a member to work with
    // so we should just bail out of this middleware. Nothing to do here.
    if (!member) return next(action)

    // if the member doesn't have a policy (inactive EB primary) or if the member doesn't
    // have an mailing address (incomplete EB enrollment), then do not fetch the rating area
    const hasPolicy = !isNil(member.policy) && !isEmpty(member.policy)
    const hasMailingAddress =
      !isNil(member.mailingAddress) && !isEmpty(member.mailingAddress)
    if (!hasPolicy || !hasMailingAddress) {
      return next(action)
    }

    dispatch(
      fetchRatingArea(
        member.mailingAddress?.zip,
        member.policy.insuranceFilingUuid,
      ),
    )

    return next(action)
  }

/** Stores the member info in local storage so we can use it for LD segmenting. */
export const handleAuthedUser =
  ({ dispatch }) =>
  next =>
  action => {
    const relevantActions = /FETCH_AUTHED_USER_SUCCESS/
    if (!isActionRelevant(action, relevantActions)) return next(action)

    // this is part of an idea for consolidating member info in localStorage
    // and also making it easier to acceess for feature flag segmenting.
    webStorage.setItem('sh:member', JSON.stringify(action.payload))

    // We store the authedUser so that we can use it to trigger the
    // LDContextIdentifier component to update the LD context.
    dispatch(storeAuthedUser(action.payload))

    return next(action)
  }

// The token the backend last accepted in this document: fetching the authed
// user with it succeeded. Until then a stored token may be one that expired
// while the tab was closed, and handing it to member-app would send the member
// to a page that 401s straight back to /login.
let verifiedToken = null

/**
 * Keeps member-app's session in step with the SPA: after every action, rebuild
 * the session and send it if anything changed. Only a token the backend has
 * accepted is sent. Logging out clears storage, so the rebuild comes back
 * `null` and nothing is sent.
 */
export const handleMemberAppSession = () => next => action => {
  const result = next(action)
  if (isActionRelevant(action, /FETCH_AUTHED_USER_SUCCESS/)) {
    verifiedToken = webStorage.getItem('token')
  }
  const session = buildMemberAppSession()
  if (session && session.member_token === verifiedToken) {
    syncMemberAppSession(session)
  }
  return result
}
